/**
 * Account Management is a re-occuring need, and we need to simplify the things that a developer
 * must do in order to perform this function.  While the base SDK has a collection of methods
 * to show and submit forms, the SDK will coordinate and manage the forms and submissions
 * to simplify the developer experience.  It is the goal of this class to simplify the
 * landscape so that although promises will be used, they will not overinflate the code base
 * required to be used by the Developer.  As a target, we would like to have engagements with
 * the SDK compact and requireing 5-7 lines of well-formatted code, including documentation.
 */
import PubSub from "pubsub-js";
import * as _cesForms from "./CESForms";
import * as _cesRequest from "./CESRequest";
import currentUser from "./CESUser";
import { FormNames, UserNotFoundError } from "./CESTypes";
import { translate } from "./Helpers";
import RecaptchaFactory from "./components/RecaptchaFactory";
import { endOverlay, setEncodedData, startOverlay } from "./CESUtils";
import { popupInit, popupExecute } from "./CESPopup";
import * as _configuration from "./CESConfiguration";
import CESAppInfo from "./CESAppInfo";
import ButtonElement from "./components/Button";
import { StepDescriptor, steppedFormFlows } from "./StepperContext";

const triggeredFormEventNames = {
  resendVerificationForm: "emailVerification",
  generateMobileOtpForm: "phoneVerification",
  generateOtp: "phoneVerification",
  postLoginForm: "requiredFields",
  otpValidated: "requiredFields",
  policyForm: "policyFields",
  otpGenerated: "otpVerification",
  validateEmail: "collectEmail",
  registerUser: "registerUser",
  loginUser: "traditionalLoginUser",
  communicationOptinsForm: "communicationOptins",
  abroadConsentForm: "abroadConsentForm",
  confirmBirthdayForm: "confirmBirthdayForm",
};

let flowSteps: StepDescriptor[] | undefined;
let managePostLoginOtpGeneratedToken: string | null = null;
let mobileSignInOtpGeneratedToken: string | null = null;

/**
 * Send the request to verify email. It does not accommodate the direct login.
 * @param {string} wrapperId - the element ID
 * @param {any?} overlayConfig. Optional
 * @param {string?} hrefTarget - location to redirect after successful submission. Optional
 * @returns {Promise} resolved if the request was performed and rejected otherwise
 */
export const emailVerification = (
  wrapperId: string,
  overlayConfig?: any,
  hrefTarget?: string
) => {
  const code = _cesForms.getParameterByName("verification_code");

  return new Promise((resolve, reject) => {
    startOverlay(overlayConfig)
      .then(() => _cesRequest.checkVerification(code!))
      .then((response) => {
        currentUser.setUserId(response.data.ciam.uuid);
        currentUser.setHashedKocid(response.data.ciam.hashedKocid);
        if (hrefTarget) window.location.href = hrefTarget;
        const message = "security.verification_code.janrain.authorized";
        _cesForms.handleResponse(wrapperId, message, { id: "response" });
        resolve("Success");
      })
      .catch((err: any) => {
        const errors = err?.data?.errors || err?.response?.errors;
        _cesForms.handleResponseError(wrapperId, errors[0] || errors);
        reject(err?.data || err?.response);
      })
      .finally(() => {
        endOverlay();
      });
  });
};

/**
 * Send the request to verify email. Process direct login (post login and sign in).
 * @param {string} wrapperId the element ID
 * @param {string} postLoginWrapperId the post login wrapper element ID
 * @param {?any} overlayConfig Optional
 * @param {?string} hrefTarget - location to redirect after successful submission. Optional
 * @param {?Function} successCallback - Function to be executed after successful form submission Optional
 * @param {?Function} failureCallback - Function to be executed if error in submission Optional
 * @returns {Promise} resolved if the request was performed and rejected otherwise
 */
export const emailVerificationWithDirectLogin = (
  wrapperId: string,
  postLoginWrapperId: string,
  overlayConfig?: any,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function
) => {
  const code = _cesForms.getParameterByName("verification_code");

  return new Promise((resolve, reject) => {
    startOverlay(overlayConfig)
      .then(() => _cesRequest.checkVerification(code!))
      .then((response) => {
        // The backend only process signin if direct_login_after_email_verification is set to true
        // in the client settings. If it's set to false (and false is the default), then it processes
        // email verification only and returns the data without tokens.
        const signedIn = response.token && response.refreshToken;

        currentUser.setUserId(response.data.ciam.uuid);
        currentUser.setHashedKocid(response.data.ciam.hashedKocid);

        if (signedIn) {
          currentUser.setAccessToken(response.token);
          currentUser.setRefreshToken(response.refreshToken);
          currentUser.persist(response.data);
        }
        return { signedIn, response };
      })
      .then((verificationResult: { signedIn: boolean; response: {} }) => {
        if (verificationResult.response && verificationResult.signedIn) {
          displayAuthorizedMessage(wrapperId, postLoginWrapperId);
          resolve(verificationResult.response);
        } else if (
          verificationResult.response &&
          !verificationResult.signedIn
        ) {
          // behave the same way as emailVerification method
          if (hrefTarget) window.location.href = hrefTarget;
          const message = "security.verification_code.janrain.authorized";
          _cesForms.handleResponse(wrapperId, message, { id: "response" });
          resolve("Success");
        }
      })
      .catch((error) => {
        const isPostLoginError =
          error.data && error.data.body && error.data.otp;

        if (isPostLoginError) {
          PubSub.subscribe("postLoginSuccess", () => {
            displayAuthorizedMessage(wrapperId, postLoginWrapperId);
            resolve(error.data);
          });
          managePostLoginErrors(
            wrapperId,
            postLoginWrapperId,
            overlayConfig,
            error.data,
            hrefTarget,
            successCallback,
            failureCallback
          );
        } else {
          reject(_cesForms.processValidationErrors(wrapperId, error));
        }
      })
      .finally(() => {
        endOverlay();
      });
  })
    .then((res: any) => {
      if (successCallback) {
        successCallback("TraditionalLoginSuccess");
      }
      return Boolean((res.token && res.refreshToken) || res.otp);
    })
    .catch(() => {
      if (failureCallback) {
        failureCallback("TraditionalLoginError");
      }
    });
};

const displayAuthorizedMessage = (
  wrapperId: string,
  postLoginWrapperId: string
) => {
  document.getElementById(wrapperId)!.style.display = "block";
  document.getElementById(postLoginWrapperId)!.style.display = "none";
  _cesForms.handleResponse(
    wrapperId,
    "security.verification_code.janrain.authorized",
    { id: "response" }
  );
};

/**
 * Renders the form and submit the request to change the password.
 * @param {string} formWrapperID - div's id for form render
 * @param {any?} overlayConfig. Optional
 * @param {string?} hrefTarget - location to redirect after successful submission. Optional
 * @param {Function?} successCallback - Function to be executed after successful form submission
 * @param {Function?} failureCallback - Function to be executed if error in submission
 * @returns {Promise} resolved if the form is render and rejected otherwise
 */
export const changePassword = async (
  formWrapperId: string,
  overlayConfig?: any,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function
) => {
  const formRef: { current: any } = { current: undefined };
  const sendForm = () => {
    manageSubmit(
      formRef.current,
      formWrapperId,
      FormNames.ChangePassword,
      true,
      overlayConfig,
      hrefTarget,
      successCallback,
      failureCallback
    );
  };

  formRef.current = await _cesForms.createFormNew(
    formWrapperId,
    FormNames.ChangePassword,
    sendForm,
    overlayConfig
  );
};

export const resendEmailVerification = async (
  formWrapperId: string,
  overlayConfig?: any,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function
) => {
  const formRef: { current: any } = { current: undefined };
  const sendForm = () => {
    manageSubmit(
      formRef.current,
      formWrapperId,
      FormNames.ResendVerification,
      false,
      overlayConfig,
      hrefTarget,
      successCallback,
      failureCallback
    );
  };
  formRef.current = await _cesForms.createFormNew(
    formWrapperId,
    FormNames.ResendVerification,
    sendForm,
    overlayConfig
  );
};

/**
 * Renders the form and submit the request to edit profile.
 * @param {string} formWrapperID - div's id for form render
 * @param {any?} overlayConfig. Optional
 * @param {string?} hrefTarget - location to redirect after successful submission. Optional
 * @param {Function?} successCallback - Function to be executed after successful form submission
 * @param {Function?} failureCallback - Function to be executed if error in submission
 * @returns {Promise} resolved if the form is render and rejected otherwise
 */
export const editProfile = (
  formWrapperId: string,
  overlayConfig?: any,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function
) => {
  let editProfileForm: any;

  const sendForm = () =>
    manageSubmit(
      editProfileForm,
      formWrapperId,
      FormNames.EditProfile,
      true,
      overlayConfig,
      hrefTarget,
      successCallback,
      failureCallback
    );

  return new Promise((resolve, reject) => {
    startOverlay(overlayConfig)
      .then(() => _cesForms.createProfileForm(formWrapperId, sendForm))
      .then((form) => {
        editProfileForm = form;
        resolve("Success");
      })
      .catch((reason) => {
        reject(reason);
      })
      .finally(() => {
        endOverlay();
      });
  });
};

/**
 * Handles register. Render form and manage submission. Considers merge account case
 * @param {string} formWrapperID. Called in other functions as wrapperId. Next location of the div
 * @param {string} postLoginWrapperId -div's id where the post login will be rendered
 * @param {any?} overlayConfig
 * @param {string?} hrefTarget the next location the user should be sent to
 * @param {Function?} successCallback - Function to be executed after successful form submission
 * @param {Function?} failureCallback - Function to be executed if error in submission
 * @returns {Promise} resolved if the register form is render and rejected otherwise
 */
export const register = (
  formWrapperId: string,
  postLoginWrapperId: string,
  overlayConfig?: any,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function,
  formData?: any,
  otp?: any,
  registerHrefTarget?: string,
) => {
  const formRef: { current: any } = { current: undefined };
  const sendForm = () =>
    new Promise((resolve, reject) => {
      _cesForms.releaseHandle();
      startOverlay(overlayConfig)
        .then(async () => {
          const freshToken = await _cesForms.getRecaptchaToken(FormNames.Registration);
          return submitFormNew(formRef.current, FormNames.Registration, {
            otp,
            ...(freshToken && { captcha: freshToken }),
          });
        })
        .then((res: any) => {
          let successCallbackResult = undefined;
          if (successCallback) {
            successCallbackResult = successCallback("success.registration");
          }
          Promise.resolve(successCallbackResult).then(() => {
            resolve("success.registration");
            if (hrefTarget && registerHrefTarget) {
              window.location.href = res.token
                ? hrefTarget
                : registerHrefTarget;
            } else if (hrefTarget) {
              window.location.href = hrefTarget;
            }
          });
        })
        .catch((reason) => {
          if (reason.data && reason.data.body && reason.data.otp) {
            managePostLoginErrors(
              formWrapperId,
              postLoginWrapperId,
              overlayConfig,
              reason.data,
              hrefTarget,
              successCallback,
              failureCallback,
              "success.registration"
            );
          } else {
            reject(_cesForms.processValidationErrors(formWrapperId, reason));
          }
        })
        .finally(() => {
          endOverlay();
        });
    }).catch((reason) => {
      if (failureCallback) {
        failureCallback(reason);
      }
    });

  return new Promise((resolve, reject) => {
    startOverlay(overlayConfig)
      .then(() =>
        _cesForms.createFormNew(
          formWrapperId,
          FormNames.Registration,
          sendForm,
          overlayConfig,
          formData,
          undefined,
          undefined,
          flowSteps
        )
      )
      .then((form) => {
        formRef.current = form;
        resolve("Success");
      })
      .catch((reason) => {
        reject(reason);
      })
      .finally(() => {
        endOverlay();
      });
  });
};

/**
 * Handles sign in. Considers mergeAccount and postLogin cases
 * @param {string} formWrapperID - div's id for form render
 * @param {string} postLoginWrapperId - div's id where the post login will be rendered
 * @param {any?} overlayConfig. Optioinal
 * @param {string?} hrefTarget - location to redirect after successful sign in. Optional
 * @param {string?} socialToken - Janrain social token used on social signin
 * @param {string?} provider - social provider's name
 * @param {Function?} successCallback - Function to be executed after successful form submission
 * @param {Function?} failureCallback - Function to be executed if error in submission
 * @returns {Promise} resolved if the form is render and rejected otherwise
 */
export const signIn = (
  formWrapperId: string,
  postLoginWrapperId: string,
  overlayConfig?: any,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function,
  formData?: any,
  otp?: any
) => {
  let signInForm: any;

  const sendForm = () =>
    new Promise((resolve, reject) => {
      _cesForms.releaseHandle();
      startOverlay(overlayConfig)
        .then(() => submitFormNew(signInForm, FormNames.SignIn, { otp }))
        .then(async (signInResponse: any) => {
          handleSignInSuccess(hrefTarget);
          resolve(signInResponse);
        })
        .catch((loginError) => {
          if (loginError.data && loginError.data.body && loginError.data.otp) {
            managePostLoginErrors(
              formWrapperId,
              postLoginWrapperId,
              overlayConfig,
              loginError.data,
              hrefTarget,
              successCallback,
              failureCallback
            );
          } else {
            reject(
              _cesForms.processValidationErrors(formWrapperId, loginError)
            );
          }
        })
        .finally(() => {
          endOverlay();
        });
    })
      .then(() => {
        if (successCallback) {
          successCallback("TraditionalLoginSuccess");
        }
      })
      .catch(() => {
        if (failureCallback) {
          failureCallback("TraditionalLoginError");
        }
      });

  const createForm = (formValues?: any) =>
    new Promise((resolve: any, reject: any) => {
      startOverlay(overlayConfig)
        .then(() =>
          _cesForms.createFormNew(
            formWrapperId,
            FormNames.SignIn,
            sendForm,
            overlayConfig,
            formData,
            formValues
          )
        )
        .then((form) => {
          signInForm = form;
        })
        .then(() => {
          resolve("Success");
        })
        .catch((reason) => {
          reject(reason);
        })
        .finally(() => {
          endOverlay();
        });
    });

  PubSub.subscribe("goBack", async (_: any, data: any) => {
    document.getElementById(postLoginWrapperId)!.style.display = "none";
    document.getElementById(formWrapperId)!.innerHTML = "";
    document.getElementById(formWrapperId)!.style.display = "block";
    await createForm(data);
  });

  return createForm();
};

/**
 * Provides Janrain widget for social signIn
 * @param {string} wrapperId - div's id where social widget renders
 * @param {string} mergeAccountWrapperID - div's id where the mergeAccount is render
 * @param {string} postLoginWrapperId - div's id where the postLogin is render
 * @param {any?} overlayConfig. Optional
 * @param {string?} hrefTarget - location to redirect after successful submission. Optional
 * @param {string?} registrationFormId. Optional.
 * @param {string?} provider. Optional.
 * @returns {Promise} resolved if the widget is render and rejected otherwise
 */
export const socialSignIn = (
  wrapperId: string,
  mergeAccountWrapperID: string,
  postLoginWrapperId: string,
  overlayConfig?: any,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function,
  registerHrefTarget?: string,
  isNewEndpoint?: boolean,
  endpointResponse?: any
) => {
  const socialResult = (response: any) => {
    const { result } = response;

    if (result.token && result.refreshToken) {
      currentUser.setAccessToken(result.token);
      currentUser.setRefreshToken(result.refreshToken);
      currentUser.setUserId(result.data.ciam.uuid);
      currentUser.setHashedKocid(result.data.ciam.hashedKocid);
      currentUser.persist(result.data);

      if (hrefTarget && currentUser.getUserId()) {
        window.location.href = hrefTarget;
      }
      if (successCallback) {
        successCallback("SocialLoginSuccess");
      }
    } else if (result.form) {
      if (result.form.form === "registrationForm") {
        if (successCallback) {
          successCallback("SocialRegistrationStart");
        }
        socialRegistration(
          mergeAccountWrapperID,
          postLoginWrapperId,
          result.form,
          result.otp,
          overlayConfig,
          registerHrefTarget ? registerHrefTarget : hrefTarget,
          successCallback,
          failureCallback
        );
        PubSub.publish("socialRegistrationStart");
      } else {
        managePostLoginErrors(
          wrapperId,
          postLoginWrapperId,
          overlayConfig,
          { body: JSON.stringify(result.form), otp: result.otp },
          hrefTarget,
          successCallback,
          failureCallback
        );
      }
    }
  };
  if (isNewEndpoint) {
    socialResult({ result: endpointResponse });
    return;
  }
  const janrainElement = document.createElement("div");
  janrainElement.id = "janrainEngageEmbed";
  janrainElement.setAttribute("name", wrapperId);
  document.getElementById(wrapperId)!.appendChild(janrainElement);

  // add hidden loading label
  const loadingLabel = document.createElement("div");
  loadingLabel.style.display = "none";

  _cesRequest
    .getBundleResource("social.provider.loading")
    .then((res) => {
      loadingLabel.innerHTML = res;
    })
    .catch((err) => {
      loadingLabel.innerHTML = err;
    });

  janrainElement.appendChild(loadingLabel);

  sessionStorage.removeItem("social-provider");

  const { socialSettings, ciamClientId } = CESAppInfo;

  const providersNames = socialSettings.providers || [];
  const providersIconPath =
    socialSettings.providersIconPath || "/assets/images/";

  const buttonContainer = document.createElement("div");
  janrainElement.appendChild(buttonContainer);

  providersNames.forEach((providerName) => {
    const providerLabel = document.createElement("span");
    providerLabel.classList.add("provider-label");

    _cesRequest
      .getBundleResource(`social.provider.${providerName}.signin`)
      .then((res) => {
        providerLabel.innerHTML = res;
      })
      .catch((err) => {
        providerLabel.innerHTML = err;
      });

    const providerIcon = new Image();
    providerIcon.classList.add("provider-icon");
    providerIcon.src = `${providersIconPath + providerName}.svg`;

    const button = document.createElement("button");
    button.classList.add("social-button");
    button.id = `social-${providerName}`;

    button.addEventListener("click", () => {
      loadingLabel.style.display = "block";
      buttonContainer.style.display = "none";

      if (successCallback) {
        successCallback("SocialLoginStart");
      }

      const config = _configuration.socialNative(providerName, ciamClientId);
      const win = !socialSettings.useRedirect ? popupInit() : null;

      // config.host is the bare hostname only (path-stripped via new URL().host),
      // so building from it drops any path prefix apiBaseUrl carries (e.g. /api).
      // apiBaseUrl already has the full scheme+host+prefix — use it directly.
      const requestUrl = `${CESAppInfo.apiBaseUrl}${config.endpoint}`;

      Promise.resolve(requestUrl).then((request) => {
          if (!socialSettings.useRedirect) {
            popupExecute(win, request, (err: any, response: any) => {
              if (err) {
                if (failureCallback) {
                  failureCallback("SocialLoginError");
                }
                buttonContainer.style.display = "block";
                loadingLabel.style.display = "none";
              } else {
                const result = JSON.parse(decodeURIComponent(response));
                socialResult({ provider: providerName, result });
              }
            });
          } else {
            setEncodedData("social-meta", { provider: providerName });
            window.location.href = request;
          }
        });
    });

    button.append(providerIcon, providerLabel);
    buttonContainer.append(button);
  });

  return Promise.resolve();
};

/**
 * Manages social login. This envolves the cases for: Social registration, merge account and error submission.
 * @param {string} hrefTarget - location to redirect after successful submission
 * @param {string} queryParameter
 */
const handleSignInSuccess = (hrefTarget?: string, queryParameter?: string) => {
  endOverlay();
  if (hrefTarget && currentUser.getUserId()) {
    hrefTarget = queryParameter
      ? `${hrefTarget} ? ${queryParameter}`
      : hrefTarget;
    window.location.href = hrefTarget;
  }
};

/**
 * Renders the form and submit request that to send email for password recovery
 * @param {string} formWrapperID - div's id for form render
 * @param {any?} overlayConfig. Optional
 * @param {string?} hrefTarget - location to redirect after successful submission. Optional
 * @param {Function?} successCallback - Function to be executed after successful form submission
 * @param {Function?} failureCallback - Function to be executed if error in submission
 * @returns {Promise} resolved if the form is render and rejected otherwise
 */
export const forgotPassword = async (
  formWrapperId: string,
  overlayConfig?: any,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function
) => {
  const formRef: { current: any } = { current: undefined };
  const sendForm = () => {
    manageSubmit(
      formRef.current,
      formWrapperId,
      FormNames.ForgotPassword,
      false,
      overlayConfig,
      hrefTarget,
      successCallback,
      failureCallback
    );
  };

  formRef.current = await _cesForms.createFormNew(
    formWrapperId,
    FormNames.ForgotPassword,
    sendForm,
    overlayConfig
  );
};

/**
 * Renders the form and submit the password change (requires valid code provided by email response)
 * @param {string} formWrapperID - div's id for form render
 * @param {any} overlayConfig. Optional
 * @param {string?} hrefTarget - location to redirect after successful submission. Optional
 * @param {Function?} successCallback - Function to be executed after successful form submission
 * @param {Function?} failureCallback - Function to be executed if error in submission
 * @returns resolved if the form is render and rejected otherwise
 */
export const resetPassword = async (
  formWrapperId: string,
  overlayConfig?: any,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function
) => {
  try {
    const formRef: { current: any } = { current: undefined };
    await startOverlay(overlayConfig);
    const code = _cesForms.getParameterByName("code");

    if (!code) {
      window.location.replace("/");
    }

    const sendForm = () =>
      manageSubmit(
        formRef.current,
        formWrapperId,
        FormNames.ResetPassword,
        false,
        overlayConfig,
        hrefTarget,
        successCallback,
        failureCallback,
        code
      );

    formRef.current = await _cesForms.createFormNew(
      formWrapperId,
      FormNames.ResetPassword,
      sendForm,
      overlayConfig,
      undefined,
      undefined,
      code
    );

    return "Success";
  } catch (error) {
    const err = error as any;
    const errorData = err?.data?.errors || err?.response?.data?.errors;
    _cesForms.handleResponseError(formWrapperId, errorData[0] || errorData);
    return "Invalid token";
  } finally {
    endOverlay();
  }
};

/**
 * Provides a front-end logout
 * @param {string} hrefTarget - location to redirect after successful submission. Optional
 * @returns {Promise} resolved if logout is successful and rejected otherwise
 */
export const logout = async (hrefTarget?: string) => {
  try {
    await _cesRequest.logOut();
  } finally {
    if (hrefTarget) {
      window.location.href = hrefTarget;
    }
  }
};

const getOtpResponseFormData = (response: any) => {
  if (response?.body?.data?.form) return response.body.data.form;
  if (response?.body?.fields) return response.body;
  if (response?.body?.form && typeof response.body.form === "object") return response.body.form;
  if (response?.data?.form) return response.data.form;
  if (response?.fields) return response;
  return response?.body;
};

const getOtpResponseFormName = (response: any) =>
  getOtpResponseFormData(response)?.form || response?.body?.form || response?.form;

const submitFormNew = async (form: any, formName: string, formValues?: any) => {
  let formJSON: any;
  if (
    formName === FormNames.GenerateOtpForm ||
    formName === FormNames.ValidateOtpForm
  ) {
    formJSON = JSON.stringify(await _cesForms.getSubmitOtpFormValues(form));
  } else {
    formJSON = JSON.stringify(await _cesForms.getFormValuesNew(form, formName));
  }
  return new Promise((resolve, reject) => {
    if (!formJSON) {
      reject("The fields are not filled correctly");
    }

    return _cesRequest
      .submitForm(formJSON, formName, formValues)
      .then(async (response: any) => {
        if (
          formName === FormNames.SignIn ||
          formName === FormNames.Registration ||
          formName === FormNames.PostLogin ||
          formName === FormNames.ValidateOtpForm
        ) {
          if (
            response &&
            response.data &&
            response.data.token &&
            response.data.refreshToken
          ) {
            currentUser.setAccessToken(response.data.token);
            currentUser.setRefreshToken(response.data.refreshToken);
            currentUser.persist(response.data.data);
            currentUser.setUserId(response.data.data.ciam.uuid);
            currentUser.setHashedKocid(response.data.data.ciam.hashedKocid);
          }
        }
        resolve(response.data);
      })
      .catch((reason: any) => {
        form.reset();
        reject(reason);
      });
  });
};

/**
 * Submit the form to CDS API.
 * @param {string} formId - div's id where form is
 * @param {string} formType - form type to be sent (enum)
 * @param {any?} userValues - user attributes that will be submitted. Optional
 * @param {boolean?} callPostLogin - if post login validations are needed. Optional
 * @returns {Promise} resolved if the form is submitted and rejected otherwise
 */
const submitAuthorizedForm = async (
  form: any,
  formType: string,
  formWrapperId: string,
  userValues?: any,
  _callPostLogin?: boolean
) => {
  const selectedForm = formType;

  const formJSON = await _cesForms.getFormValuesNew(
    form,
    formType,
    "",
    userValues
  );
  const recaptcha = RecaptchaFactory.getInstance();

  return new Promise((resolve, reject) => {
    if (!formJSON) {
      return reject("The fields are not filled correctly");
    }

    if (recaptcha?.isApplied?.() && !recaptcha.isVerified()) {
      // v2 checkbox rendered but not solved — surface it instead of crashing
      // (no element class implements execute(); a bare call was a TypeError
      // that silently killed the submit click).
      endOverlay();
      reject("captchaNotVerified");
    } else {
      if (recaptcha?.isApplied?.()) {
        recaptcha.reset();
      }

      const config = userValues
        ? _configuration.editProfileConfig(userValues)
        : _configuration.submitFormConfig(
          JSON.stringify(formJSON),
          selectedForm
        );

      return _cesRequest
        .putRequest(`${CESAppInfo.apiBaseUrl}${config.endpoint}`, config)
        .then((response) => {
          resolve(response);
        })
        .catch((error: any) => {
          try {
            const { errors } = error.data;
            errors.forEach((error: any) => {
              _cesForms.handleResponseError(formWrapperId, error);
            });

            try {
              reject(errors[0].message);
            } catch {
              reject("unkownError");
            }
            endOverlay();
          } catch {
            reject("unexpectedError");
          }
        });
    }
  });
};

/**
 * Submit a form. If error ocurrs, it display error messages
 * @param {string} formWrapperId - div's id for form render
 * @param {string} formName
 * @param {boolean} needsAuthorization - To use the apropiate method
 * @param {any?} overlayConfig. Optional
 * @param {string?} hrefTarget - location to redirect after successful submission. Optional
 * @param {Function?} successCallback - Function to be executed after successful form submission
 * @param {Function?} failureCallback - Function to be executed if error in submission
 * @returns {Promise} resolved if the form is send and rejected otherwise
 */
export const manageSubmit = (
  form: any,
  formWrapperId: string,
  formName: string,
  needsAuthorization: boolean,
  overlayConfig?: any,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function,
  otp?: string
) =>
  new Promise((resolve, reject) => {
    _cesForms.releaseHandle();
    startOverlay(overlayConfig)
      .then(() => {
        if (needsAuthorization) {
          return submitAuthorizedForm(
            form,
            formName,
            formWrapperId,
            undefined,
            true
          );
        }

        return submitFormNew(form, formName, { otp });
      })
      .then(async (response: any) => {
        if (formName === FormNames.EditProfile) {
          currentUser.persist(response.data.data);
        }

        if (hrefTarget) {
          window.location.href = hrefTarget;
        }

        resolve("Success");
      })
      .catch((reason) => {
        reject(_cesForms.processValidationErrors(formWrapperId, reason));
      })
      .finally(() => {
        endOverlay();
      });
  })
    .then(() => {
      if (successCallback) {
        successCallback();
      }
    })
    .catch(() => {
      if (failureCallback) {
        failureCallback();
      }
    });

/**
 * Request user info by uiid
 * @returns {Promise} resolved if the user was found and the query was successful, rejected otherwise
 */
export const getUserInfo = async () => {
  const token = await _cesRequest.getAccessToken();

  if (!token) {
    return Promise.reject(UserNotFoundError);
  }

  const response = await _cesRequest.getUserInfo();
  currentUser.persist(response.data);

  return response;
};

const socialRegistration = (
  formWrapperId: string,
  postLoginWrapperId: string,
  registrationForm: any,
  otp: string,
  overlayConfig?: any,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function
) => {
  let socialRegistrationForm: any;
  flowSteps = registrationForm?.isExtraZone ? undefined : steppedFormFlows.socialRegistration;

  const sendForm = async () => {
    _cesForms.releaseHandle();
    await startOverlay(overlayConfig);

    try {
      const freshToken = await _cesForms.getRecaptchaToken(FormNames.Registration);
      const res = await submitFormNew(
        socialRegistrationForm,
        FormNames.Registration,
        {
          otp,
          ...(freshToken && { captcha: freshToken }),
        }
      );

      successCallback?.("SocialRegistrationSuccess");

      const resolvedValue =
        res === "hasConfirmedEmail"
          ? "success.registration.emailVerification.required"
          : "success.registration";

      return resolvedValue;
    } catch (reason: any) {
      if (reason.data?.body && reason.data?.otp) {
        managePostLoginErrors(
          formWrapperId,
          postLoginWrapperId,
          overlayConfig,
          reason.data,
          hrefTarget,
          successCallback,
          failureCallback,
          "success.registration"
        );
        return "handled";
      }
      throw _cesForms.processValidationErrors(formWrapperId, reason);
    } finally {
      endOverlay();
    }
  };

  return _cesForms
    .createFormNew(
      formWrapperId,
      FormNames.Registration,
      sendForm,
      overlayConfig,
      registrationForm,
      undefined,
      undefined,
      flowSteps
    )
    .then((form: any) => {
      socialRegistrationForm = form;
      endOverlay({ id: "cds-mobile-sign-in-form" });
    })
    .catch((reason: any) => {
      if (failureCallback) failureCallback(reason);
      throw reason;
    });
};

const generateOtp = async (
  wrapperId: any,
  overlayConfig: any,
  formValues?: any,
  serviceOtp?: any,
) => {
  document.getElementById(wrapperId)!.innerHTML = "";

  const generateOtpFormSubmit = async () => {
    try {
      _cesForms.releaseHandle();
      await startOverlay(overlayConfig);
      const values = await generateOtpForm.getValues();

      // Explicitly force a fresh token — the form metadata may not include a
      // captcha field, and the backend requires one on every OTP request.
      const freshToken = await _cesForms.getRecaptchaToken(FormNames.GenerateOtpForm);

      await submitFormNew(generateOtpForm, FormNames.GenerateOtpForm, {
        ...(freshToken && { captcha: freshToken }),
      });

      PubSub.publish("otpGenerated", { formValues: values, otp: serviceOtp });
    } catch (error) {
      _cesForms.handleResponseError(wrapperId, (error as any).data.errors[0]);
    } finally {
      endOverlay();
    }
  };

  const generateOtpForm = await _cesForms.createFormNew(
    wrapperId,
    FormNames.GenerateOtpForm,
    generateOtpFormSubmit,
    overlayConfig,
    undefined,
    formValues,
    undefined,
    flowSteps
  );

  const text = document.createTextNode(translate("label.otp.mobile.disclose"));
  const infoElement = document.createElement("p");

  infoElement.appendChild(text);
  document.getElementById(wrapperId)!.appendChild(infoElement);
};

const validateOtp = async (
  wrapperId: string,
  postLoginWrapperId: string,
  overlayConfig: any,
  formValues: any,
  otp?: string,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function,
  onResend?: () => Promise<void>,
) => {
  document.getElementById(wrapperId)!.innerHTML = "";

  const validateOtpFormSubmit = async () => {
    try {
      _cesForms.releaseHandle();
      await startOverlay(overlayConfig);
      const input = `+${formValues.mobileNumberCountryCode}${formValues.mobile}`;

      // Explicitly force a fresh token for OTP validation
      const freshToken = await _cesForms.getRecaptchaToken(FormNames.ValidateOtpForm);

      const response: any = await submitFormNew(
        validateOtpForm,
        FormNames.ValidateOtpForm,
        {
          input,
          ...(otp && { serviceOtp: otp }),
          ...(freshToken && { captcha: freshToken })
        }
      );

      response.token
        ? PubSub.publish("loginSuccess", response)
        : PubSub.publish("otpValidated", response);
    } catch (error) {
      const err = error as any;
      console.error("[reCAPTCHA] OTP validation error:", err);
      if (err.data && err.data.body && err.data.otp) {
        managePostLoginErrors(
          wrapperId,
          postLoginWrapperId,
          overlayConfig,
          err.data,
          hrefTarget,
          successCallback,
          failureCallback
        );
      } else {
        _cesForms.handleResponseError(wrapperId, err.data.errors[0]);
      }
    } finally {
      endOverlay();
    }
  };

  const resendOtpSubmit = async () => {
    if (onResend) {
      await onResend();
    } else {
      PubSub.publish("generateOtp", { formValues, serviceOtp: otp });
    }
  };

  const validateOtpForm = await _cesForms.createFormNew(
    wrapperId,
    FormNames.ValidateOtpForm,
    validateOtpFormSubmit,
    overlayConfig,
    undefined,
    undefined,
    undefined,
    flowSteps
  );

  const text = document.createTextNode(translate("label.otp.verify"));
  const infoElement = document.createElement("p");

  const mergedAccountForm = document.getElementById(wrapperId);
  infoElement.appendChild(text);

  const { element: control } = new ButtonElement(
    "submit-resend",
    translate("i18nStrings.resend.otp"),
    ["ces-form-submit", "button-primary"],
    resendOtpSubmit
  );

  if (mergedAccountForm) {
    const signInForm = document.getElementById("ces-sign-in-form");
    const mobileSignInForm = document.getElementById("cds-mobile-sign-in-form");
    if (signInForm) {
      signInForm.style.display = "none";
    }
    if (mobileSignInForm) {
      mobileSignInForm.style.display = "none";
    }
    mergedAccountForm.style.display = "block";
    mergedAccountForm.appendChild(infoElement);
    mergedAccountForm.appendChild(control);
  }
};

const validateEmail = async (
  wrapperId: string,
  data: any,
  overlayConfig: any
) => {
  document.getElementById(wrapperId)!.innerHTML = "";
  const validateEmailFormSubmit = async () => {
    try {
      _cesForms.releaseHandle();
      await startOverlay(overlayConfig);

      // Explicitly force a fresh token — same requirement as the other OTP steps.
      const freshToken = await _cesForms.getRecaptchaToken(FormNames.ValidateEmailForm);

      const response: any = await submitFormNew(
        validateEmailForm,
        FormNames.ValidateEmailForm,
        { otp: data.otp, ...(freshToken && { captcha: freshToken }) }
      );

      PubSub.publish(
        getOtpResponseFormName(response) === FormNames.Registration
          ? "register"
          : "traditionalLogin",
        response
      );
    } catch (error) {
      _cesForms.handleResponseError(wrapperId, (error as any).data.errors[0]);
    } finally {
      endOverlay();
    }
  };

  const validateEmailForm = await _cesForms.createFormNew(
    wrapperId,
    FormNames.ValidateEmailForm,
    validateEmailFormSubmit,
    overlayConfig,
    getOtpResponseFormData(data),
    undefined,
    undefined,
    flowSteps
  );
};

export const mobileSignIn = async (
  wrapperId: string,
  postLoginWrapperId: string,
  mergeAccountWrapperID: string,
  overlayConfig?: any,
  registerHrefTarget?: string,
  experienceHrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function
) => {
  PubSub.subscribe("generateOtp", async (_: any, data: any) => {
    const { formValues, serviceOtp } = data;
    if (successCallback) {
      successCallback({ formName: triggeredFormEventNames["generateOtp"] });
    }
    await generateOtp(wrapperId, overlayConfig, formValues, serviceOtp);
    await socialSignIn(
      wrapperId,
      mergeAccountWrapperID,
      postLoginWrapperId,
      overlayConfig,
      experienceHrefTarget,
      successCallback,
      failureCallback,
      registerHrefTarget
    );
  });

  mobileSignInOtpGeneratedToken = PubSub.subscribe("otpGenerated", async (_: any, data: any) => {
    await startOverlay({ id: "cds-mobile-sign-in-form" });

    if (successCallback) {
      successCallback({ formName: triggeredFormEventNames["otpGenerated"] });
    }

    const formValues = data?.formValues ?? data;
    const otp = data?.otp;

    await validateOtp(
      wrapperId,
      postLoginWrapperId,
      overlayConfig,
      formValues,
      otp,
      experienceHrefTarget,
      successCallback,
      failureCallback
    );
  });

  PubSub.subscribe("otpValidated", async (_: any, data: any) => {
    if (successCallback) {
      if (getOtpResponseFormName(data) === FormNames.Registration) {
        successCallback({ formName: triggeredFormEventNames["registerUser"] });
        document.getElementById(wrapperId)!.innerHTML = "";
        await register(
          wrapperId,
          postLoginWrapperId,
          overlayConfig,
          experienceHrefTarget,
          successCallback,
          failureCallback,
          getOtpResponseFormData(data),
          data.otp,
          registerHrefTarget,
        );
      } else {
        flowSteps = getOtpResponseFormData(data)?.isExtraZone
          ? undefined
          : steppedFormFlows.mobileOtp;
        successCallback({ formName: triggeredFormEventNames["validateEmail"] });
        await validateEmail(wrapperId, data, overlayConfig);
      }
    }
  });

  PubSub.subscribe("register", async (_: any, data: any) => {
    if (successCallback) {
      successCallback({ formName: triggeredFormEventNames["registerUser"] });
    }

    document.getElementById(wrapperId)!.innerHTML = "";
    await register(
      wrapperId,
      postLoginWrapperId,
      overlayConfig,
      experienceHrefTarget,
      successCallback,
      failureCallback,
      getOtpResponseFormData(data),
      data.otp,
      registerHrefTarget,
    );
  });

  PubSub.subscribe("traditionalLogin", async (_: any, data: any) => {
    if (successCallback) {
      successCallback({ formName: triggeredFormEventNames["loginUser"] });
    }

    endOverlay({ id: "step-indicators" });
    document.getElementById(wrapperId)!.innerHTML = "";
    await signIn(
      wrapperId,
      postLoginWrapperId,
      overlayConfig,
      experienceHrefTarget,
      successCallback,
      failureCallback,
      getOtpResponseFormData(data),
      data.otp
    );
  });

  PubSub.subscribe("loginSuccess", () => {
    if (successCallback) {
      successCallback("Success");
    }

    handleSignInSuccess(experienceHrefTarget);
  });

  PubSub.subscribe("goBack", async (_: any, data: any) => {
    if (successCallback) {
      successCallback({ formName: triggeredFormEventNames["generateOtp"] });
    }

    document.getElementById(postLoginWrapperId)!.style.display = "none";
    document.getElementById(wrapperId)!.innerHTML = "";
    document.getElementById(wrapperId)!.style.display = "block";

    await generateOtp(wrapperId, overlayConfig, data);
    await socialSignIn(
      wrapperId,
      mergeAccountWrapperID,
      postLoginWrapperId,
      overlayConfig,
      experienceHrefTarget,
      successCallback,
      failureCallback,
      registerHrefTarget
    );
  });

  if (successCallback) {
    successCallback({ formName: triggeredFormEventNames["generateOtp"] });
  }

  await generateOtp(wrapperId, overlayConfig);
  await socialSignIn(
    wrapperId,
    mergeAccountWrapperID,
    postLoginWrapperId,
    overlayConfig,
    experienceHrefTarget,
    successCallback,
    failureCallback,
    registerHrefTarget
  );
};

const verifyEmailAddress = async (
  wrapperId: string,
  overlayConfig: any,
  form: any,
  otp: string
) => {
  const verifyEmailAddressSubmit = async () => {
    try {
      _cesForms.releaseHandle();
      await startOverlay(overlayConfig);

      await submitFormNew(verifyEmailForm, FormNames.ResendVerification, {
        otp,
      });

      const text = document.createTextNode(
        translate("account.email.verification.resend")
      );
      const infoElement = document.createElement("p");
      if (
        document.getElementById(wrapperId)?.lastChild?.nodeName !==
        infoElement.nodeName
      ) {
        infoElement.appendChild(text);
        document.getElementById(wrapperId)?.appendChild(infoElement);
      }
    } catch (error) {
      _cesForms.handleResponseError(wrapperId, (error as any).data.errors[0]);
    } finally {
      endOverlay();
    }
  };

  const text = document.createTextNode(translate("label.verify.email.info"));
  const infoElement = document.createElement("p");

  infoElement.appendChild(text);
  document.getElementById(wrapperId)!.appendChild(infoElement);

  const verifyEmailForm = await _cesForms.createFormNew(
    wrapperId,
    FormNames.ResendVerification,
    verifyEmailAddressSubmit,
    overlayConfig,
    form
  );

  const goBackSubmit = async () =>
    PubSub.publish("goBack", await verifyEmailForm.getValues());

  const { element: control } = new ButtonElement(
    "submit-go-back",
    translate("i18nStrings.go.back"),
    ["ces-form-submit", "button-primary"],
    goBackSubmit
  );
  document.getElementById(wrapperId)!.appendChild(control);
};

const verifyMobile = async (
  wrapperId: any,
  overlayConfig: any,
  form: any,
  otp: string,
  formValues?: any,
) => {
  document.getElementById(wrapperId)!.innerHTML = "";

  const verifyMobileSubmit = async () => {
    try {
      _cesForms.releaseHandle();
      await startOverlay(overlayConfig);
      const values = await verifyMobileForm.getValues();

      await submitFormNew(verifyMobileForm, FormNames.GenerateOtpForm, { otp });

      PubSub.publish("otpGenerated", {
        formValues: values,
        otp,
      });
    } catch (error) {
      _cesForms.handleResponseError(wrapperId, (error as any).data.errors[0]);
    } finally {
      endOverlay();
    }
  };

  const text = document.createTextNode(translate("label.mobile.verify"));
  const infoElement = document.createElement("p");

  infoElement.appendChild(text);
  document.getElementById(wrapperId)!.appendChild(infoElement);

  const verifyMobileForm = await _cesForms.createFormNew(
    wrapperId,
    FormNames.GenerateOtpForm,
    verifyMobileSubmit,
    overlayConfig,
    form,
    formValues,
    undefined,
    flowSteps
  );
};

const collectRequiredFields = async (
  wrapperId: string,
  overlayConfig: any,
  form: any,
  otp: string
) => {
  document.getElementById(wrapperId)!.innerHTML = "";

  const postLoginSubmit = async () => {
    try {
      _cesForms.releaseHandle();
      await startOverlay(overlayConfig);
      const response: any = await submitFormNew(
        postLoginForm,
        FormNames.PostLogin,
        { otp }
      );

      if (response.token) {
        PubSub.publish("loginSuccess");
      }
    } catch (error) {
      _cesForms.handleResponseError(wrapperId, (error as any).data.errors[0]);
    } finally {
      endOverlay();
    }
  };

  const postLoginForm = await _cesForms.createFormNew(
    wrapperId,
    FormNames.PostLogin,
    postLoginSubmit,
    overlayConfig,
    form
  );
};


const confirmCommunicationOptins = async (
  wrapperId: string,
  postLoginWrapperId: string,
  overlayConfig: any,
  form: any,
  otp: string,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function
) => {
  document.getElementById(wrapperId)!.innerHTML = "";

  const communicationOptinsSubmit = async () => {
    try {
      _cesForms.releaseHandle();
      await startOverlay(overlayConfig);
      const response: any = await submitFormNew(
        communicationOptinsForm,
        FormNames.PostLogin,
        { otp }
      );

      if (response.token) {
        PubSub.publish("loginSuccess");
      }
    } catch (error) {
      const err = error as any;
      if (err.data && err.data.body && err.data.otp) {
        managePostLoginErrors(
          wrapperId,
          postLoginWrapperId,
          overlayConfig,
          err.data,
          hrefTarget,
          successCallback,
          failureCallback
        );
      } else {
        _cesForms.handleResponseError(postLoginWrapperId, err.data.errors[0]);
      }
    } finally {
      endOverlay();
    }
  };

  const text = document.createTextNode(
    translate("label.communicationOptins.agree")
  );
  const infoElement = document.createElement("p");

  infoElement.appendChild(text);
  document.getElementById(postLoginWrapperId)!.appendChild(infoElement);

  const communicationOptinsForm = await _cesForms.createFormNew(
    postLoginWrapperId,
    FormNames.PostLogin,
    communicationOptinsSubmit,
    overlayConfig,
    form
  );
};

const collectValidBirthday = async (
  wrapperId: string,
  postLoginWrapperId: string,
  overlayConfig: any,
  form: any,
  otp: string,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function
) => {
  document.getElementById(wrapperId)!.innerHTML = "";

  const postLoginSubmit = async () => {
    try {
      _cesForms.releaseHandle();
      await startOverlay(overlayConfig);
      const response: any = await submitFormNew(
        postLoginForm,
        FormNames.PostLogin,
        { otp }
      );

      if (response.token) {
        PubSub.publish("loginSuccess");
      }
    } catch (error) {
      const err = error as any;
      if (err.data && err.data.body && err.data.otp) {
        managePostLoginErrors(
          wrapperId,
          postLoginWrapperId,
          overlayConfig,
          err.data,
          hrefTarget,
          successCallback,
          failureCallback
        );
      } else {
        _cesForms.handleResponseError(postLoginWrapperId, err.data.errors[0]);
      }
    } finally {
      endOverlay();
    }
  };

  const loadingLabel = document.createElement("div");
  loadingLabel.innerHTML = translate("label.collectValidBirthday");
  document.getElementById(postLoginWrapperId)!.appendChild(loadingLabel);

  const postLoginForm = await _cesForms.createFormNew(
    postLoginWrapperId,
    FormNames.PostLogin,
    postLoginSubmit,
    overlayConfig,
    form
  );
};

let rejectConfirmed = false;
const overlayConfigModal = {
  id: "abroad-consent-modal-overlay",
  backgroundColor: "white",
  display: "block",
  opacity: "0.5",
};
const abroadConsentModalId = "abroad-consent-reject-form-modal";

const confirmAbroadConsent = async (
  wrapperId: string,
  postLoginWrapperId: string,
  overlayConfig: any,
  form: any,
  otp: string,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function
) => {
  document.getElementById(wrapperId)!.innerHTML = "";

  const abroadConsentSubmit = async () => {
    try {
      _cesForms.releaseHandle();
      await startOverlay(overlayConfig);
      const abroadConsentAccepted = abroadConsentForm
        .getDOM()
        .querySelector("#checkboxAbroadConsentAccept").checked;
      const abroadConsentRejected = abroadConsentForm
        .getDOM()
        .querySelector("#checkboxAbroadConsentReject").checked;
      const abroadConsentSubmitButton = abroadConsentForm
        .getDOM()
        .querySelector("#ces-form-submit-ces-post-login");

      if (
        (abroadConsentAccepted && !abroadConsentRejected) ||
        (abroadConsentRejected && rejectConfirmed)
      ) {
        try {
          const response: any = await submitFormNew(
            abroadConsentForm,
            FormNames.PostLogin,
            { otp }
          );
          if (response.token) {
            PubSub.publish("loginSuccess");
          }
        } catch (error) {
          if ((error as any).status === 302) {
            window.location.href = "/";
            return;
          }
          throw error;
        }
      } else if (abroadConsentAccepted === abroadConsentRejected) {
        // Show UI message
        return;
      } else if (abroadConsentRejected) {
        const abroadConsentModal =
          document.getElementById(abroadConsentModalId);
        if (!abroadConsentModal) {
          await buildModal();
          await abroadConsentReject(
            abroadConsentModalId,
            abroadConsentSubmitButton,
            overlayConfig
          );
        }
      }
    } catch (error) {
      const err = error as any;
      if (err.data && err.data.body && err.data.otp) {
        managePostLoginErrors(
          wrapperId,
          postLoginWrapperId,
          overlayConfig,
          err.data,
          hrefTarget,
          successCallback,
          failureCallback
        );
      } else {
        _cesForms.handleResponseError(postLoginWrapperId, err.data.errors[0]);
      }
    } finally {
      endOverlay();
    }
  };

  const loadingLabel = document.createElement("div");
  loadingLabel.innerHTML = translate("label.abroadConsent.agree");
  document.getElementById(postLoginWrapperId)!.appendChild(loadingLabel);

  const abroadConsentForm = await _cesForms.createFormNew(
    postLoginWrapperId,
    FormNames.PostLogin,
    abroadConsentSubmit,
    overlayConfig,
    form
  );
};

const abroadConsentReject = async (
  postLoginWrapperId: string,
  submitButton: any,
  overlayConfig: any
) => {
  const form = {
    form: "abroadConsentConfirmForm",
    fields: [
      {
        name: "rejectAbroadConsentConfirm",
        type: "checkbox",
        label: "",
        validation: {
          messages: {},
        },
        schemaAttribute: "",
        value: false,
      },
      {
        name: "rejectAbroadConsentCancel",
        type: "checkbox",
        label: "",
        validation: {
          messages: {},
        },
        schemaAttribute: "",
        value: false,
      },
    ],
  };
  const abroadConsentRejectSubmit = async () => {
    let removeModal = true;
    try {
      _cesForms.releaseHandle();
      const abroadConsentRejectConfirmed = abroadConsentRejectForm
        .getDOM()
        .querySelector("#rejectAbroadConsentConfirm").checked;
      const abroadConsentRejectCanceled = abroadConsentRejectForm
        .getDOM()
        .querySelector("#rejectAbroadConsentCancel").checked;
      if (abroadConsentRejectConfirmed && !abroadConsentRejectCanceled) {
        await modalDestroy();
        rejectConfirmed = true;
        submitButton.click();
      } else if (abroadConsentRejectConfirmed === abroadConsentRejectCanceled) {
        // Show UI message
        removeModal = false;
      } else if (abroadConsentRejectCanceled) {
        await modalDestroy();
      }
    } catch (error) {
      console.error("Error during abroad consent form processing:", error);
    } finally {
      removeModal && modalDestroy();
    }
  };

  const loadingLabel = document.createElement("div");
  loadingLabel.innerHTML = translate("label.abroadConsentReject.confirm");
  document.getElementById(postLoginWrapperId)!.appendChild(loadingLabel);

  await startOverlay(overlayConfigModal);
  const abroadConsentRejectForm = await _cesForms.createFormNew(
    postLoginWrapperId,
    FormNames.PostLogin,
    abroadConsentRejectSubmit,
    overlayConfig,
    form
  );
};

const modalDestroy = async () => {
  const modal = document.getElementById(abroadConsentModalId);
  modal?.remove();
  const modalOverlay = document.getElementById(overlayConfigModal.id);
  modalOverlay?.remove();
};

const buildModal = async () => {
  const body = document.querySelector("body")!;

  const modalOverlay = document.createElement("div");
  modalOverlay.id = "abroad-consent-modal-overlay";
  modalOverlay.style.width = "100%";
  modalOverlay.style.height = "100%";
  modalOverlay.style.position = "fixed";
  modalOverlay.style.display = "none";
  modalOverlay.style.top = "0";
  modalOverlay.style.left = "0";
  modalOverlay.style.right = "0";
  modalOverlay.style.bottom = "0";
  modalOverlay.style.zIndex = "999";
  modalOverlay.style.cursor = "pointer";
  body.appendChild(modalOverlay);

  const innerDiv = document.createElement("div");
  innerDiv.id = abroadConsentModalId;
  body.appendChild(innerDiv);
  innerDiv.style.display = "block";
  innerDiv.style.padding = "10px";
  innerDiv.style.zIndex = "99999999999";
  innerDiv.style.width = "300px";
  innerDiv.style.height = "320px";
  innerDiv.style.position = "absolute";
  innerDiv.style.left = "50%";
  innerDiv.style.top = "50%";
  innerDiv.style.transform = "translate(-50%, -50%)";
  innerDiv.style.backgroundColor = "rgba(255,255,255)";
  innerDiv.style.border = "1px solid";

  const closeButton = document.createElement("span");
  closeButton.innerHTML = "&times;";
  closeButton.style.color = "#aaa";
  closeButton.style.float = "right";
  closeButton.style.lineHeight = "0.5";
  closeButton.style.fontSize = "50px";
  closeButton.style.fontWeight = "bold";
  closeButton.style.marginRight = "-5px";
  closeButton.style.cursor = "pointer";
  closeButton.onmouseover = (event) => {
    const element = event.target as HTMLSpanElement;
    element.style.color = "#ccc";
  };
  closeButton.onmouseleave = (event) => {
    const element = event.target as HTMLSpanElement;
    element.style.color = "#aaa";
  };
  closeButton.onclick = async () => {
    modalDestroy();
  };
  innerDiv.appendChild(closeButton);
};

/**
 * Handles post-login form submission after registration
 */
const confirmPostLogin = async (
  postLoginWrapperId: string,
  overlayConfig: any,
  form: any,
  otp: string
): Promise<void> => {
  const postLoginSubmit = async () => {
    try {
      _cesForms.releaseHandle();
      await startOverlay(overlayConfig);
      const response: any = await submitFormNew(
        postLoginForm,
        FormNames.PostLogin,
        { otp }
      );

      if (response.token) {
        PubSub.publish("loginSuccess");
      }
    } catch (err: any) {
      if (err.data?.errors?.length > 0) {
        _cesForms.handleResponseError(postLoginWrapperId, err.data.errors[0]);
      } else {
        _cesForms.handleResponseError(postLoginWrapperId, "generic.error");
      }
    } finally {
      endOverlay(overlayConfig);
    }
  };

  const postLoginForm = await _cesForms.createFormNew(
    postLoginWrapperId,
    FormNames.PostLogin,
    postLoginSubmit,
    overlayConfig,
    form
  );
};

const managePostLoginErrors = async (
  wrapperId: string,
  postLoginWrapperId: string,
  overlayConfig: any,
  data: any,
  hrefTarget?: string,
  successCallback?: Function,
  failureCallback?: Function,
  callbackMessage?: string,
) => {
  const { body, otp } = data;
  const form = JSON.parse(body);

  // Clear both wrappers to ensure clean state
  const wrapperEl = document.getElementById(wrapperId);
  const postLoginEl = document.getElementById(postLoginWrapperId);

  if (wrapperEl && postLoginEl) {
    wrapperEl.innerHTML = "";
    wrapperEl.style.display = "none";
    postLoginEl.innerHTML = "";
    postLoginEl.style.display = "block";
  }

  // Force cleanup of any existing reCAPTCHA
  const recaptcha = RecaptchaFactory.getInstance();
  if (recaptcha.dispose) {
    recaptcha.dispose();
  }

  interface StartFlowMap {
    [key: string]: () => Promise<void>;
    resendVerificationForm: () => Promise<void>;
    generateMobileOtpForm: () => Promise<void>;
    postLoginForm: () => Promise<void>;
    communicationOptinsForm: () => Promise<void>;
    abroadConsentForm: () => Promise<void>;
    confirmBirthdayForm: () => Promise<void>;
  }

  const startFlow: StartFlowMap = {
    resendVerificationForm: async () =>
      await verifyEmailAddress(postLoginWrapperId, overlayConfig, form, otp),
    generateMobileOtpForm: async () =>
      await verifyMobile(postLoginWrapperId, overlayConfig, form, otp),
    postLoginForm: async () =>
      await confirmPostLogin(postLoginWrapperId, overlayConfig, form, otp),
    communicationOptinsForm: async () =>
      await confirmCommunicationOptins(
        wrapperId,
        postLoginWrapperId,
        overlayConfig,
        form,
        otp,
        hrefTarget,
        successCallback,
        failureCallback
      ),
    abroadConsentForm: async () =>
      await confirmAbroadConsent(
        wrapperId,
        postLoginWrapperId,
        overlayConfig,
        form,
        otp,
        hrefTarget,
        successCallback,
        failureCallback
      ),
    confirmBirthdayForm: async () =>
      await collectValidBirthday(
        wrapperId,
        postLoginWrapperId,
        overlayConfig,
        form,
        otp,
        hrefTarget,
        successCallback,
        failureCallback
      ),
  };

  if (mobileSignInOtpGeneratedToken) {
    PubSub.unsubscribe(mobileSignInOtpGeneratedToken);
    mobileSignInOtpGeneratedToken = null;
  }

  if (managePostLoginOtpGeneratedToken) {
    PubSub.unsubscribe(managePostLoginOtpGeneratedToken);
  }

  managePostLoginOtpGeneratedToken = PubSub.subscribe("otpGenerated", async (_: any, data: any) => {
    if (successCallback) {
      successCallback({ formName: triggeredFormEventNames["otpGenerated"] });
    }

    const postLoginElement = document.getElementById(postLoginWrapperId);
    if (postLoginElement) {
      postLoginElement.innerHTML = "";
      postLoginElement.style.display = "none";
    }

    const handleResend = async () => {
      const wrapperEl = document.getElementById(wrapperId);
      if (wrapperEl) {
        wrapperEl.innerHTML = "";
        wrapperEl.style.display = "block";
      }
      await generateOtp(wrapperId, overlayConfig, data.formValues, data.otp);
    };

    await validateOtp(
      wrapperId,
      postLoginWrapperId,
      overlayConfig,
      data.formValues,
      data.otp,
      hrefTarget,
      successCallback,
      failureCallback,
      handleResend,
    );
  });

  PubSub.subscribe("otpValidated", async (_: any, data: any) => {
    if (successCallback) {
      successCallback({ formName: triggeredFormEventNames["otpValidated"] });
    }

    await collectRequiredFields(postLoginWrapperId, overlayConfig, form, data.otp);
  });

  PubSub.unsubscribe("loginSuccess");
  PubSub.subscribe("loginSuccess", () => {
    PubSub.publish("postLoginSuccess");
    let successCallbackResult = undefined;
    if (successCallback) {
      successCallbackResult = successCallback(
        callbackMessage ? callbackMessage : "Success"
      );
    }
    Promise.resolve(successCallbackResult).then(() => {
      if (hrefTarget) {
        handleSignInSuccess(hrefTarget);
      }
    });
  });

  // Type-check the form.form before using it
  if (form.form && typeof form.form === 'string' && form.form in startFlow) {
    await startFlow[form.form]();

    if (successCallback && form.form in triggeredFormEventNames) {
      successCallback({
        formName: triggeredFormEventNames[form.form as keyof typeof triggeredFormEventNames]
      });
    }
  } else if (form.form) {
    // Without this an unrecognised form name renders nothing and reports nothing.
    console.warn(`CES: no post-login flow registered for form "${form.form}"`);
  }
};
